import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChatCircleDots, PaperPlaneRight, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS_STYLE = {
  delivered: "border-emerald-500/25 bg-emerald-500/10 text-emerald-400",
  sent: "border-blue-500/25 bg-blue-500/10 text-blue-400",
  accepted: "border-blue-500/25 bg-blue-500/10 text-blue-400",
  queued: "border-amber-500/25 bg-amber-500/10 text-amber-400",
  sending: "border-amber-500/25 bg-amber-500/10 text-amber-400",
  failed: "border-red-500/25 bg-red-500/10 text-red-400",
  undelivered: "border-red-500/25 bg-red-500/10 text-red-400",
  skipped: "border-zinc-700 bg-zinc-800/60 text-zinc-400",
};

const STATUS_LABEL = {
  delivered: "Delivered",
  sent: "Sent",
  accepted: "Sent",
  queued: "Queued",
  sending: "Sending",
  failed: "Failed",
  undelivered: "Undelivered",
  skipped: "Skipped",
};

export const SmsPanel = ({ entity, entityId, defaultTo, presets = [] }) => {
  const [configured, setConfigured] = useState(null);
  const [to, setTo] = useState(defaultTo || "");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [lineType, setLineType] = useState(null);

  const loadMessages = useCallback(() => {
    api
      .get("/sms/messages", { params: { entity, entity_id: entityId } })
      .then(({ data }) => {
        setMessages(data.items || []);
        if (
          (data.items || []).some((m) => m.direction === "inbound" && !m.read)
        )
          api
            .post("/sms/read", null, {
              params: { entity, entity_id: entityId },
            })
            .catch(() => {});
      })
      .catch(() => setMessages([]));
  }, [entity, entityId]);

  useEffect(() => {
    api
      .get("/sms/status")
      .then(({ data }) => setConfigured(data.configured))
      .catch(() => setConfigured(false));
    loadMessages();
  }, [loadMessages]);

  useEffect(() => setTo(defaultTo || ""), [defaultTo]);

  useEffect(() => {
    const v = (to || "").trim();
    if (v.length < 7 || configured === false) {
      setLineType(null);
      return;
    }
    const id = setTimeout(() => {
      api
        .get("/sms/line-type", { params: { phone: v } })
        .then(({ data }) => setLineType(data))
        .catch(() => setLineType(null));
    }, 600);
    return () => clearTimeout(id);
  }, [to, configured]);

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/sms/send", { to, body, entity, entity_id: entityId });
      toast.success("Text sent");
      setBody("");
      loadMessages();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not send the text",
      );
    }
    setBusy(false);
  };

  return (
    <div
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
      data-testid="sms-panel"
    >
      <div className="flex items-center gap-2">
        <ChatCircleDots size={16} weight="bold" className="text-indigo-300" />
        <div className="text-xs uppercase tracking-widest text-zinc-500">
          Texts
        </div>
        {messages.some((m) => m.direction === "inbound") && (
          <span
            data-testid="sms-reply-count"
            className="rounded-full border border-indigo-400/40 bg-indigo-500/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-indigo-300"
          >
            {messages.filter((m) => m.direction === "inbound").length} replies
          </span>
        )}
      </div>

      {configured === false ? (
        <p
          className="mt-3 text-sm text-zinc-500"
          data-testid="sms-not-configured"
        >
          Texting is not set up yet. Add your Twilio Account SID, Auth Token and
          sending number to switch this on.
        </p>
      ) : (
        <>
          <Input
            data-testid="sms-to-input"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="+13145550123"
            className="mt-3 h-12 border-zinc-800 bg-zinc-950"
          />
          {lineType && lineType.sms_capable === false && (
            <p
              data-testid="sms-line-type-flag"
              className="mt-2 flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300"
            >
              <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
              <span>
                {lineType.label}. A text to this number will most likely not be
                delivered.
              </span>
            </p>
          )}
          {presets.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {presets.map((p) => (
                <button
                  key={p.label}
                  data-testid={`sms-preset-${p.label.toLowerCase().replace(/ /g, "-")}`}
                  onClick={() => setBody(p.text)}
                  className="rounded-full border border-zinc-800 bg-zinc-950 px-3 py-1 text-[10px] uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-300"
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
          <Textarea
            data-testid="sms-body-input"
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Hi — quick update on your countertops…"
            className="mt-2 border-zinc-800 bg-zinc-950"
          />
          <Button
            data-testid="sms-send-button"
            disabled={busy || !to.trim() || !body.trim()}
            onClick={send}
            className="mt-3 h-12 w-full bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
          >
            <PaperPlaneRight size={15} weight="bold" className="mr-2" />
            {busy ? "Sending…" : "Send text"}
          </Button>
        </>
      )}

      {messages.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="sms-history">
          {messages.map((m) => {
            const inbound = m.direction === "inbound";
            return (
              <div
                key={m.id}
                data-testid={`sms-message-${m.id}`}
                className={`rounded-md border p-3 ${
                  inbound
                    ? "border-indigo-500/30 bg-indigo-500/5"
                    : "border-zinc-800 bg-zinc-950"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono-data text-[11px] text-zinc-500">
                    {inbound ? `Reply from ${m.from}` : m.to}
                  </span>
                  <span
                    className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                      inbound
                        ? "border-indigo-400/40 bg-indigo-500/10 text-indigo-300"
                        : STATUS_STYLE[m.status] ||
                          "border-zinc-700 text-zinc-400"
                    }`}
                  >
                    {inbound ? "Reply" : STATUS_LABEL[m.status] || m.status}
                  </span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-zinc-200">
                  {m.body}
                </p>
                {m.error && (
                  <p className="mt-1 text-xs text-red-400">{m.error}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
